import { useEffect, useRef } from "react";
import type { UseFormSetValue, FieldArrayWithId, UseFieldArrayRemove } from "react-hook-form";
import type { SubscriptionFormData } from "@/pages/subscriptions/services/types";
import type { SubscriptionMetricsResponse } from "@/pages/subscriptions/services/types";
import { SubscriptionType } from "@/pages/subscriptions/constants";

/**
 * Custom hook that handles clearing dependent form fields when source, metric, or subscription type changes.
 * - When subscription type changes: clears source, metric, and filters if current selection is not valid
 * - When source changes: clears metric and all filters
 * - When metric changes: clears all filters
 */
export const useFormFieldReset = (
  source: string,
  metric: string,
  setValue: UseFormSetValue<SubscriptionFormData>,
  fields: FieldArrayWithId<SubscriptionFormData, "filters">[],
  remove: UseFieldArrayRemove,
  subscriptionType?: SubscriptionType,
  metricsData?: SubscriptionMetricsResponse
): void => {
  // Track previous values to detect changes
  const prevSourceRef = useRef<string>();
  const prevMetricRef = useRef<string>();
  const prevSubscriptionTypeRef = useRef<string>();
  const isInitialMount = useRef(true);

  // Clear source/metric when subscription type changes if current selection is not valid
  useEffect(() => {
    if (isInitialMount.current) {
      prevSubscriptionTypeRef.current = subscriptionType;
      return;
    }

    if (
      prevSubscriptionTypeRef.current !== undefined &&
      prevSubscriptionTypeRef.current !== subscriptionType &&
      subscriptionType &&
      metricsData
    ) {
      // Check if current metric is still valid for new subscription type
      if (metric) {
        const currentMetric = metricsData.find(m => m.name === metric);
        if (
          currentMetric &&
          !currentMetric.supported_subscription_types.includes(subscriptionType)
        ) {
          // Clear source and metric
          setValue("source", "");
          setValue("source_name", "");
          setValue("platform", "");
          setValue("metric", "");

          // Clear all filters
          if (fields.length > 0) {
            for (let i = fields.length - 1; i >= 0; i--) {
              remove(i);
            }
          }
        }
      }
    }

    prevSubscriptionTypeRef.current = subscriptionType;
  }, [subscriptionType, metric, metricsData, setValue, fields.length, remove]);

  // Clear metric and filters when source changes
  useEffect(() => {
    if (isInitialMount.current) {
      // Skip on initial mount
      prevSourceRef.current = source;
      prevMetricRef.current = metric;
      isInitialMount.current = false;
      return;
    }

    if (prevSourceRef.current !== undefined && prevSourceRef.current !== source) {
      if (!source) {
        setValue("source_name", "");
        setValue("platform", "");
      }

      // Clear metric field
      setValue("metric", "");

      // Clear all filters
      if (fields.length > 0) {
        // Remove all filters by removing from end to start
        for (let i = fields.length - 1; i >= 0; i--) {
          remove(i);
        }
      }
    }

    prevSourceRef.current = source;
  }, [source, metric, setValue, fields.length, remove]);

  // Clear filters when metric changes
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }

    if (prevMetricRef.current !== undefined && prevMetricRef.current !== metric) {
      // Clear all filters
      if (fields.length > 0) {
        // Remove all filters by removing from end to start
        for (let i = fields.length - 1; i >= 0; i--) {
          remove(i);
        }
      }
    }

    prevMetricRef.current = metric;
  }, [metric, fields.length, remove]);
};
